/**
 * Flap Portal 支付底启用状态（getQuoteTokenConfiguration.enabled）
 * 普通部署 CRYPTO / RWA 点选以此为准：未启用 = 灰不可点。
 */
import { createPublicClient, http, fallback, parseAbi, zeroAddress } from "viem";
import { bsc } from "viem/chains";
import { USDT_BSC, USDC_BSC, readErc20Meta, validateTokenBnbV2Liquidity } from "./bnbPairCheck.js";
import { BNB_QUOTE } from "./bnbVaultData.js";
import { FLAP_TRADE_PORTAL_56 } from "./flapSalt.js";

const PORTAL = FLAP_TRADE_PORTAL_56;

const quoteConfigAbi = parseAbi([
  "function getQuoteTokenConfiguration(address quoteToken) view returns ((uint8 enabled, uint8 defaultCurve, uint8 alternativeCurve, uint8 nativeToQuoteSwapType, uint8 dexId))",
]);

/** 站内静态图（scripts/fetch-corla-quote-logos.mjs 从 Corla / Flap 同源拉取） */
export const QUOTE_LOGO_BASE = "/images/quotes";

function quoteLogo(file) {
  return `${QUOTE_LOGO_BASE}/${file}`;
}

/** 与 Flap launch CRYPTO 顺序一致（含 USDC） */
export const FLAP_CRYPTO_QUOTE_OPTIONS = [
  {
    id: "bnb",
    symbol: "BNB",
    address: BNB_QUOTE,
    logo: quoteLogo("bnb.png"),
  },
  {
    id: "usdt",
    symbol: "USDT",
    address: USDT_BSC,
    logo: quoteLogo("usdt.png"),
  },
  {
    id: "usdc",
    symbol: "USDC",
    address: USDC_BSC,
    logo: quoteLogo("usdc.png"),
  },
  {
    id: "usd1",
    symbol: "USD1",
    address: "0x8d0D000Ee44948FC98c9B98A4FA4921476f08B0d",
    logo: quoteLogo("usd1.png"),
  },
  {
    id: "u",
    symbol: "U",
    address: "0xcE24439F2D9C6a2289F741120FE202248B666666",
    logo: quoteLogo("u.png"),
  },
  {
    id: "btcb",
    symbol: "BTCB",
    address: "0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c",
    logo: quoteLogo("btcb.png"),
  },
  {
    id: "sol",
    symbol: "SOL",
    address: "0x570A5D26f7765Ecb712C0924E4De545B89fD43dF",
    logo: quoteLogo("sol.png"),
  },
  {
    id: "eth",
    symbol: "ETH",
    address: "0x2170Ed0880ac9A755fd29B2688956BD959F933F8",
    logo: quoteLogo("eth.svg"),
  },
];

/**
 * 离线兜底：最近一次 Portal 探测到的已启用 CRYPTO symbol（主网）
 * 运行时仍会 eth_call 刷新。
 */
export const FLAP_ENABLED_CRYPTO_SYMBOLS_FALLBACK = [
  "BNB",
  "USDT",
  "USDC",
  "USD1",
  "U",
  "BTCB",
  "SOL",
  "ETH",
];

/**
 * 离线兜底：最近一次 Portal 探测到的已启用 RWA symbol（主网）
 * 探测 2026-09-14 · 28 个
 */
export const FLAP_ENABLED_RWA_SYMBOLS_FALLBACK = [
  "SPCXB",
  "SKHYB",
  "SPYB",
  "QQQB",
  "NVDAB",
  "AAPLB",
  "AMZNB",
  "TSLAB",
  "MSFTB",
  "METAB",
  "MSTRB",
  "HOODB",
  "XAUT",
  "BABAB",
  "GMEB",
  "GOOGLB",
  "DJTB",
  "NFLXB",
  "NOKB",
  "TSMB",
  "SOXLB",
  "SOXSB",
  "MRNAB",
  "FLNCB",
  "BMNRB",
  "FXION",
  "SNXXB",
  "TQQQB",
];

function publicClient() {
  return createPublicClient({
    chain: bsc,
    transport: fallback([
      http("https://bsc.publicnode.com", { timeout: 8_000 }),
      http("/api/rpc.php", { timeout: 5_000 }),
      http("https://bsc-dataseed.binance.org/", { timeout: 8_000 }),
    ]),
  });
}

const probeCache = new Map();
const PROBE_CACHE_MS = 45_000;

function normAddr(a) {
  return String(a || "").toLowerCase();
}

function probeListKey(list) {
  return list.map((a) => normAddr(a)).sort().join(",");
}

function getProbeCache(key) {
  const hit = probeCache.get(key);
  if (hit && Date.now() - hit.at < PROBE_CACHE_MS) return hit.set;
  return null;
}

function setProbeCache(key, set) {
  probeCache.set(key, { set, at: Date.now() });
}

async function multicallQuoteProbe(client, contracts, list, pick, errMsg) {
  let okCount = 0;
  let failCount = 0;
  const out = new Set();
  try {
    const results = await client.multicall({ contracts, allowFailure: true });
    for (let idx = 0; idx < list.length; idx++) {
      const r = results[idx];
      if (r.status === "success") {
        okCount += 1;
        if (pick(r.result, list[idx])) out.add(normAddr(list[idx]));
      } else {
        failCount += 1;
      }
    }
  } catch {
    failCount = list.length;
  }
  if (okCount === 0 && failCount > 0) throw new Error(errMsg);
  return out;
}

/** Portal / 工厂读 quote 时：原生 BNB 用 zeroAddress */
export function portalQuoteArg(addr) {
  const a = normAddr(addr);
  if (!a || a === normAddr(BNB_QUOTE)) return zeroAddress;
  return addr;
}

/**
 * @param {string} quoteToken
 * @returns {Promise<boolean>}
 */
export async function isFlapQuoteEnabled(quoteToken) {
  if (quoteToken == null || quoteToken === "") return false;
  const target = portalQuoteArg(quoteToken);
  try {
    const cfg = await publicClient().readContract({
      address: PORTAL,
      abi: quoteConfigAbi,
      functionName: "getQuoteTokenConfiguration",
      args: [target],
    });
    return Number(cfg?.enabled ?? 0) === 1;
  } catch {
    return false;
  }
}

const factoryQuoteAbi = parseAbi([
  "function isQuoteTokenSupported(address quoteToken) view returns (bool)",
]);

/**
 * 金库工厂是否支持该支付底（链上 isQuoteTokenSupported）
 * @param {string} factory
 * @param {string} quoteToken
 */
export async function isVaultQuoteSupported(factory, quoteToken) {
  const f = String(factory || "").trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(f)) return false;
  try {
    return await publicClient().readContract({
      address: f,
      abi: factoryQuoteAbi,
      functionName: "isQuoteTokenSupported",
      args: [portalQuoteArg(quoteToken)],
    });
  } catch {
    return false;
  }
}

/**
 * 批量探测金库工厂支持的支付底
 * @returns {Promise<Set<string>>} lowercase 地址集；全失败时抛错
 */
export async function fetchVaultSupportedQuoteSet(factory, addresses) {
  const f = String(factory || "").trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(f)) {
    return new Set();
  }
  const list = [
    ...new Set(
      (addresses || [])
        .map((a) => String(a || "").trim())
        .filter((a) => /^0x[a-fA-F0-9]{40}$/i.test(a) || a === BNB_QUOTE)
    ),
  ];
  if (!list.length) return new Set();

  const cacheKey = `factory:${normAddr(f)}:${probeListKey(list)}`;
  const cached = getProbeCache(cacheKey);
  if (cached) return cached;

  const client = publicClient();
  const supported = await multicallQuoteProbe(
    client,
    list.map((addr) => ({
      address: f,
      abi: factoryQuoteAbi,
      functionName: "isQuoteTokenSupported",
      args: [portalQuoteArg(addr)],
    })),
    list,
    (r) => r,
    "Vault quote probe failed"
  );
  setProbeCache(cacheKey, supported);
  return supported;
}

/**
 * 批量探测 Portal 已启用支付底
 * @param {string[]} addresses
 * @returns {Promise<Set<string>>} 已启用地址 lowercase set
 */
export async function fetchFlapEnabledQuoteSet(addresses) {
  const list = [
    ...new Set(
      (addresses || [])
        .map((a) => String(a || "").trim())
        .filter((a) => /^0x[a-fA-F0-9]{40}$/i.test(a))
    ),
  ];
  if (!list.length) return new Set();

  const cacheKey = `portal:${probeListKey(list)}`;
  const cached = getProbeCache(cacheKey);
  if (cached) return cached;

  const enabled = await multicallQuoteProbe(
    publicClient(),
    list.map((addr) => ({
      address: PORTAL,
      abi: quoteConfigAbi,
      functionName: "getQuoteTokenConfiguration",
      args: [portalQuoteArg(addr)],
    })),
    list,
    (cfg) => Number(cfg?.enabled ?? 0) === 1,
    "Flap quote probe failed"
  );
  setProbeCache(cacheKey, enabled);
  return enabled;
}

/** Flap 自定义 Quote：DexScreener 总流动性下限（USD）；第三方数据，可能偏差 */
export const MIN_CUSTOM_QUOTE_LIQ_USD = 5_000;
/** 无 Dex 数据时：Pancake V2 BNB 单边下限 */
export const MIN_CUSTOM_QUOTE_BNB = 1;

function formatLiqUsd(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(2)}K`;
  if (v > 0) return `$${v.toFixed(0)}`;
  return "";
}

/**
 * DexScreener BSC 流动性（与 Flap「第三方流动性」一致；失败返回 0）
 * @param {string} token
 */
export async function fetchDexScreenerLiqUsd(token) {
  const addr = String(token || "").trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(addr)) return 0;
  try {
    const res = await fetch(
      `https://api.dexscreener.com/latest/dex/tokens/${addr}`,
      { cache: "no-store" }
    );
    if (!res.ok) return 0;
    const j = await res.json();
    const pairs = Array.isArray(j?.pairs) ? j.pairs : [];
    let best = 0;
    for (const p of pairs) {
      if (String(p?.chainId || "").toLowerCase() !== "bsc") continue;
      const liq = Number(p?.liquidity?.usd) || 0;
      if (liq > best) best = liq;
    }
    return best;
  } catch {
    return 0;
  }
}

/**
 * Flap Permissionless 自定义支付底资格：
 * - Portal 已启用 → 直接通过
 * - 否则按流动性（DexScreener 或 V2 BNB）判定，对齐 flap.sh「资格检查」
 *
 * @param {string} quoteToken
 * @returns {Promise<{
 *   ok: boolean,
 *   address: string,
 *   symbol: string,
 *   name: string,
 *   decimals: number,
 *   portalEnabled: boolean,
 *   liqUsd: number,
 *   bnbReserve: number,
 *   message: string,
 *   messageEn: string,
 * }>}
 */
export async function checkFlapCustomQuoteEligibility(quoteToken) {
  const addr = String(quoteToken || "").trim();
  const empty = {
    ok: false,
    address: addr,
    symbol: "",
    name: "",
    decimals: 18,
    portalEnabled: false,
    liqUsd: 0,
    bnbReserve: 0,
    message: "请填写有效的代币合约地址",
    messageEn: "Invalid token address",
  };
  if (!/^0x[a-fA-F0-9]{40}$/i.test(addr)) return empty;
  if (normAddr(addr) === normAddr(BNB_QUOTE) || normAddr(addr) === normAddr(zeroAddress)) {
    return {
      ...empty,
      message: "原生 BNB 请走 CRYPTO Tab",
      messageEn: "Use the CRYPTO tab for native BNB",
    };
  }

  const [meta, portalEnabled, liqUsd, v2] = await Promise.all([
    readErc20Meta(addr),
    isFlapQuoteEnabled(addr),
    fetchDexScreenerLiqUsd(addr),
    validateTokenBnbV2Liquidity(addr, {
      minBnb: MIN_CUSTOM_QUOTE_BNB,
      exclusive: false,
    }),
  ]);

  const symbol = meta.symbol || v2.symbol || `${addr.slice(0, 6)}…`;
  const name = meta.name || symbol;
  const decimals = meta.decimals ?? 18;
  const bnbReserve = Number(v2.bnbReserve) || 0;
  const base = {
    address: addr,
    symbol,
    name,
    decimals,
    portalEnabled: !!portalEnabled,
    liqUsd: Number(liqUsd) || 0,
    bnbReserve,
  };

  if (portalEnabled) {
    const liqLabel = formatLiqUsd(liqUsd) || (bnbReserve > 0 ? `${bnbReserve.toFixed(2)} BNB` : "");
    return {
      ...base,
      ok: true,
      message: liqLabel
        ? `资格检查通过 · ${symbol} · 流动性 ${liqLabel}`
        : `资格检查通过 · ${symbol}`,
      messageEn: liqLabel
        ? `Eligible · ${symbol} · liquidity ${liqLabel}`
        : `Eligible · ${symbol}`,
    };
  }

  const dexOk = liqUsd >= MIN_CUSTOM_QUOTE_LIQ_USD;
  const v2Ok = !!v2.ok && bnbReserve >= MIN_CUSTOM_QUOTE_BNB;
  if (dexOk || v2Ok) {
    const liqLabel =
      formatLiqUsd(liqUsd) ||
      (bnbReserve > 0 ? `${bnbReserve.toFixed(2)} BNB (V2)` : "");
    return {
      ...base,
      ok: true,
      message: `资格检查通过 · ${symbol}${liqLabel ? ` · 流动性 ${liqLabel}` : ""}`,
      messageEn: `Eligible · ${symbol}${liqLabel ? ` · liquidity ${liqLabel}` : ""}`,
    };
  }

  if (!v2.ok && liqUsd <= 0) {
    return {
      ...base,
      ok: false,
      message:
        v2.message ||
        `${symbol} 流动性不足或无法读取（需 Pancake V2 BNB 对或 Dex 流动性）`,
      messageEn:
        v2.messageEn ||
        `${symbol} lacks readable liquidity (need V2 BNB pair or Dex liquidity)`,
    };
  }

  return {
    ...base,
    ok: false,
    message: `${symbol} 流动性不足（建议 Dex ≥ $${MIN_CUSTOM_QUOTE_LIQ_USD.toLocaleString()} 或 V2 BNB ≥ ${MIN_CUSTOM_QUOTE_BNB}）`,
    messageEn: `${symbol} liquidity too low (need Dex ≥ $${MIN_CUSTOM_QUOTE_LIQ_USD.toLocaleString()} or V2 BNB ≥ ${MIN_CUSTOM_QUOTE_BNB})`,
  };
}

